// frontend/src/pages/MatchScoring/WicketModal.js
//
// Captures all info for a wicket on a regular ball.
// On confirm, returns:
//   { dismissalType, dismissedPlayerId, fielderId, fielder2Id, nextBatterId,
//     drsTakenBy, drsOriginalDecision, drsDecision }
//
// The parent decides how to wire this into the /balls payload.
//
// Constraints:
// - dismissalType is required
// - fielderId required for caught/run_out/stumped
// - fielder2Id optional (catch & bowled assist; run-out throw + breaker)
// - nextBatterId required (the next batter coming in)
// - DRS: if toggled, both upheld/overturned/umpires_call require taken_by

import React, { useMemo, useState } from 'react';

const DISMISSAL_TYPES = [
  { v: 'bowled',     label: 'Bowled',      needsFielder: false },
  { v: 'caught',     label: 'Caught',      needsFielder: true  },
  // Caught by the wicket keeper. Sent as 'caught_behind'; the server stores it
  // as 'caught' with the keeper as fielder, which is what makes it count as a
  // keeper catch (fielding_figures.caught_behind) and print "c †Keeper b Bowler".
  { v: 'caught_behind', label: 'Caught behind', needsFielder: true },
  { v: 'lbw',        label: 'LBW',         needsFielder: false },
  { v: 'run_out',    label: 'Run out',     needsFielder: true  },
  { v: 'stumped',    label: 'Stumped',     needsFielder: true  },
  { v: 'hit_wicket', label: 'Hit wicket',  needsFielder: false },
  { v: 'retired_out', label: 'Retired out', needsFielder: false },
  // Retired hurt is NOT out — handled specially by the parent (calls the
  // retire-hurt endpoint, not a wicket ball). No fielder, no next batter needed.
  { v: 'retired_hurt', label: 'Retired hurt', needsFielder: false, notOut: true },
];

export default function WicketModal({
  striker, nonStriker,
  bowlingSquad,
  battingSquad,            // for "next batter" picker (filter out already-out)
  alreadyOutMemberIds,     // Set
  membersById,
  drsEnabled,              // whether match_drs_config.enabled is true
  drsBatterChances,        // remaining
  onCancel, onConfirm,
}) {
  const [dismissalType, setDt] = useState('bowled');
  const [dismissedId,   setDi] = useState(striker?.id || '');
  const [fielderId,     setFi] = useState('');
  const [fielder2Id,    setF2] = useState('');
  const [nextBatterId,  setNx] = useState('');
  const [drsKind,       setDk] = useState('no');   // 'no' | 'upheld' | 'overturned' | 'umpires_call'
  // Run-out only: runs completed before the dismissal, and whether they were
  // off the bat (credited to the striker) or byes/leg-byes (extras).
  const [runOutRuns,    setRoR] = useState(0);     // 0..4
  const [runOutType,    setRoT] = useState('bat'); // 'bat' | 'bye' | 'legbye'

  const dt = DISMISSAL_TYPES.find(d => d.v === dismissalType) || DISMISSAL_TYPES[0];
  const needsFielder = dt.needsFielder;
  const keeperOnly = dismissalType === 'stumped' || dismissalType === 'caught_behind';
  const keeperRow = (bowlingSquad || []).find(s => s.is_keeper);

  // Choosing a type: caught behind / stumped pre-select the current keeper.
  function pickType(v) {
    setDt(v);
    if ((v === 'caught_behind' || v === 'stumped') && keeperRow) setFi(keeperRow.member_id);
    else if (v !== dismissalType) setFi('');
  }

  const nameOf = (id) => membersById.get(id)?.display_name || (id ? id.slice(0, 8) : '');
  const battingNames = useMemo(() => {
    return battingSquad
      // The Sanjha Billa bats for both sides, so he is a candidate in either innings.
      // Drop anyone already dismissed in this innings.
      .filter(s => !alreadyOutMemberIds.has(s.member_id))
      // Drop the two batters currently at the crease.
      .filter(s => s.member_id !== striker?.id && s.member_id !== nonStriker?.id)
      // Sort by batting_order if set, then pick_order, then end of list.
      // For a fresh UCC App squad, batting_order is null and pick_order is the
      // alternating-draft position — that's the right ordering for "next in".
      .sort((a, b) => {
        const ao = a.batting_order ?? a.pick_order ?? 999;
        const bo = b.batting_order ?? b.pick_order ?? 999;
        return ao - bo;
      });
  }, [battingSquad, alreadyOutMemberIds, striker, nonStriker]);

  // If there's nobody left to bat, the next-batter pick is no longer required.
  // The captain can still save the dismissal — they're then expected to tap
  // "End innings" in the bottom bar (per UCC charter, no auto-end).
  const noOneLeft = battingNames.length === 0;

  // Retired hurt is not out: no fielder ever needed; the "next batter" here is
  // the REPLACEMENT taking the vacated end (optional — the scorer can also leave
  // the end empty and fill it later). So it's allowed to confirm without one.
  const isRetiredHurt = dt.notOut === true;

  const canConfirm = dismissalType && dismissedId
    && (isRetiredHurt
        ? true
        : ((!needsFielder || fielderId) && (noOneLeft || nextBatterId)));

  function confirm() {
    if (!canConfirm) return;
    if (isRetiredHurt) {
      // Signal the parent to route this to the retire-hurt endpoint instead of a
      // wicket ball. nextBatterId (if chosen) is the replacement batsman.
      onConfirm({
        isRetiredHurt: true,
        dismissedPlayerId: dismissedId,
        replacementId: nextBatterId || null,
      });
      return;
    }
    onConfirm({
      dismissalType,
      dismissedPlayerId: dismissedId,
      fielderId:  needsFielder ? fielderId : null,
      fielder2Id: needsFielder ? (fielder2Id || null) : null,
      nextBatterId: nextBatterId || null,
      runOutRuns:    dismissalType === 'run_out' ? runOutRuns : 0,
      runOutRunType: dismissalType === 'run_out' ? runOutType : 'bat',
      drsTakenBy:        drsKind === 'no' ? null : 'batter',
      drsOriginalDecision: drsKind === 'no' ? null : 'out',
      drsDecision:       drsKind === 'no' ? null : drsKind,
    });
  }

  return (
    <div className="sc-modal-backdrop" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="sc-modal">
        <div className="sc-modal-body">
        <h3>Wicket</h3>

        <h4>Dismissal type</h4>
        <div className="sc-chip-row">
          {DISMISSAL_TYPES.map(d => (
            <button
              key={d.v}
              className={`sc-chip sc-danger ${dismissalType === d.v ? 'sc-active' : ''}`}
              onClick={() => pickType(d.v)}
            >
              {d.label}
            </button>
          ))}
        </div>

        <h4>Out batter</h4>
        <div className="sc-chip-row">
          {striker && (
            <button
              className={`sc-chip ${dismissedId === striker.id ? 'sc-active' : ''}`}
              onClick={() => setDi(striker.id)}
            >
              {nameOf(striker.id)} *
            </button>
          )}
          {nonStriker && (
            <button
              className={`sc-chip ${dismissedId === nonStriker.id ? 'sc-active' : ''}`}
              onClick={() => setDi(nonStriker.id)}
            >
              {nameOf(nonStriker.id)}
            </button>
          )}
        </div>

        {needsFielder && (
          <>
            <h4>Fielder {keeperOnly ? '(keeper)' : ''}</h4>
            <div className="sc-chip-row">
              {bowlingSquad
                /* A sub can field a catch / run-out, but only keep wicket if
                   marked can_keep — so hide non-keeper subs for stumpings and
                   catches behind. The current keeper is listed first. */
                .filter(s => !(keeperOnly && s.is_sub && !s.can_keep))
                .slice()
                .sort((a, b) => (keeperOnly ? (b.is_keeper ? 1 : 0) - (a.is_keeper ? 1 : 0) : 0))
                .map(s => (
                <button
                  key={s.member_id}
                  className={`sc-chip ${fielderId === s.member_id ? 'sc-active' : ''}`}
                  onClick={() => setFi(s.member_id)}
                >
                  {nameOf(s.member_id)}{s.is_keeper ? ' (wk)' : ''}{s.is_sub ? ' (sub)' : ''}
                </button>
              ))}
            </div>
            {dismissalType === 'run_out' && (
              <>
                <h4>Run-out assist (optional)</h4>
                <div className="sc-chip-row">
                  <button className={`sc-chip ${fielder2Id === '' ? 'sc-active' : ''}`} onClick={() => setF2('')}>— none —</button>
                  {bowlingSquad.filter(s => s.member_id !== fielderId).map(s => (
                    <button
                      key={s.member_id}
                      className={`sc-chip ${fielder2Id === s.member_id ? 'sc-active' : ''}`}
                      onClick={() => setF2(s.member_id)}
                    >
                      {nameOf(s.member_id)}{s.is_sub ? ' (sub)' : ''}
                    </button>
                  ))}
                </div>

                <h4>Runs completed before run-out</h4>
                <div className="sc-chip-row">
                  {[0, 1, 2, 3, 4].map(n => (
                    <button
                      key={n}
                      className={`sc-chip ${runOutRuns === n ? 'sc-active' : ''}`}
                      onClick={() => setRoR(n)}
                    >
                      {n}
                    </button>
                  ))}
                </div>

                {runOutRuns > 0 && (
                  <>
                    <h4>Those runs were</h4>
                    <div className="sc-chip-row">
                      <button className={`sc-chip ${runOutType === 'bat' ? 'sc-active' : ''}`} onClick={() => setRoT('bat')}>Off the bat</button>
                      <button className={`sc-chip ${runOutType === 'bye' ? 'sc-active' : ''}`} onClick={() => setRoT('bye')}>Byes</button>
                      <button className={`sc-chip ${runOutType === 'legbye' ? 'sc-active' : ''}`} onClick={() => setRoT('legbye')}>Leg byes</button>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}

        <h4>Next batter</h4>
        {noOneLeft ? (
          <div style={{
            padding: '10px 12px',
            background: '#fffbeb',
            border: '1px solid #fde68a',
            borderRadius: 8,
            fontSize: 13,
            color: '#92400e',
            lineHeight: 1.4,
          }}>
            <strong>All batters out.</strong> Save this wicket, then tap{' '}
            <strong>End innings ▸</strong> in the bottom bar.
          </div>
        ) : (
          <div className="sc-chip-row">
            {battingNames.map(s => (
              <button
                key={s.member_id}
                className={`sc-chip ${nextBatterId === s.member_id ? 'sc-active' : ''}`}
                onClick={() => setNx(s.member_id)}
              >
                {nameOf(s.member_id)}
              </button>
            ))}
          </div>
        )}

        {drsEnabled && (
          <>
            <h4 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>DRS reviewed?</span>
              {drsBatterChances != null && (
                <span style={{ fontWeight: 500, color: 'var(--sc-text-mute)', textTransform: 'none', letterSpacing: 0, fontSize: 11 }}>
                  Batter has {drsBatterChances} left
                </span>
              )}
            </h4>
            <div className="sc-chip-row">
              <button className={`sc-chip ${drsKind === 'no' ? 'sc-active' : ''}`} onClick={() => setDk('no')}>No</button>
              <button className={`sc-chip ${drsKind === 'upheld' ? 'sc-active' : ''}`} onClick={() => setDk('upheld')}>Batter (upheld)</button>
              <button className={`sc-chip ${drsKind === 'umpires_call' ? 'sc-active' : ''}`} onClick={() => setDk('umpires_call')}>Umpire's call</button>
              <button className={`sc-chip ${drsKind === 'overturned' ? 'sc-active' : ''}`} onClick={() => setDk('overturned')}>Batter (overturned)</button>
            </div>
          </>
        )}
        </div>{/* /sc-modal-body */}
        <div className="sc-modal-actions">
          <button className="sc-submit" disabled={!canConfirm} onClick={confirm}>
            {!canConfirm
              ? (needsFielder && !fielderId
                  ? 'Pick fielder'
                  : (!nextBatterId && !noOneLeft ? 'Pick next batter' : 'Save wicket'))
              : 'Save wicket'}
          </button>
          <button className="sc-cancel" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
